/* GSAP motion layer for the landing page.
   Design rules this module lives by:
   - The page must be fully readable with this module doing nothing at all
     (reduced motion, jsdom, or any runtime error) — so every hidden initial
     state is set here, never in CSS.
   - Everything is scoped to the page root and torn down via cleanup(), so
     route changes never leak ScrollTriggers or listeners.
   - Content that arrives async (plans, CMS) is armed later via refresh(). */
/* dist (UMD) imports so both webpack and Jest can load GSAP. */
import { gsap } from "gsap/dist/gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";

/* ScrollTrigger touches window.matchMedia when registered, which doesn't
   exist at module-import time in jsdom — so register lazily, at first init. */
let registered = false;
const ensureRegistered = () => {
  if (!registered) {
    gsap.registerPlugin(ScrollTrigger);
    registered = true;
    // Dev-only escape hatch for debugging motion from the console.
    if (process.env.NODE_ENV === "development" && typeof window !== "undefined") {
      window.__lpGsap = gsap;
    }
  }
};

const prefersReduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const finePointer = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(pointer: fine)").matches;

/* Hide + batch-reveal every [data-reveal] that hasn't been armed yet.
   Runs again whenever async content (pricing plans) lands. */
const armReveals = (root) => {
  const fresh = Array.from(root.querySelectorAll("[data-reveal]:not([data-lp-armed])"));
  if (!fresh.length) return;
  fresh.forEach((el) => el.setAttribute("data-lp-armed", ""));

  // Pricing cards get a heavier 3D rise; everything else the standard lift.
  const plans = fresh.filter((el) => el.classList.contains("lp-plan"));
  const rest = fresh.filter((el) => !el.classList.contains("lp-plan"));

  if (rest.length) {
    gsap.set(rest, { autoAlpha: 0, y: 36 });
    ScrollTrigger.batch(rest, {
      start: "top 88%",
      once: true,
      onEnter: (batch) =>
        gsap.to(batch, {
          autoAlpha: 1,
          y: 0,
          duration: 0.9,
          ease: "power3.out",
          stagger: 0.08,
          overwrite: true,
          // Hand transform/opacity back to CSS so :hover lifts still work.
          clearProps: "all",
        }),
    });
  }

  if (plans.length) {
    gsap.set(plans, { autoAlpha: 0, y: 70, rotationX: -14, transformPerspective: 1000, transformOrigin: "50% 100%" });
    ScrollTrigger.batch(plans, {
      start: "top 86%",
      once: true,
      onEnter: (batch) =>
        gsap.to(batch, {
          autoAlpha: 1,
          y: 0,
          rotationX: 0,
          duration: 1.1,
          ease: "power3.out",
          stagger: 0.1,
          overwrite: true,
          clearProps: "all",
        }),
    });
  }
};

/* Opening sequence — header, masked headline words, copy, product panel. */
const buildIntro = (root) => {
  const words = root.querySelectorAll(".lp-word__inner");
  const tl = gsap.timeline({ defaults: { ease: "power3.out", duration: 1 } });

  tl.from(".lp-header", { yPercent: -130, autoAlpha: 0, duration: 0.8, clearProps: "all" }, 0)
    .from(".lp-eyebrow", { y: 26, autoAlpha: 0, duration: 0.7, clearProps: "all" }, 0.15);
  if (words.length) {
    tl.from(words, {
      yPercent: 118,
      rotate: 5,
      transformOrigin: "0% 100%",
      duration: 1.1,
      ease: "power4.out",
      stagger: 0.07,
    }, 0.25);
  }
  tl.from(".lp-hero__lede", { y: 30, autoAlpha: 0, clearProps: "all" }, 0.55)
    .from(".lp-checks li", { x: -22, autoAlpha: 0, stagger: 0.09, duration: 0.7, clearProps: "all" }, 0.65)
    .from(".lp-hero__ctas > *", { y: 24, autoAlpha: 0, stagger: 0.09, clearProps: "all" }, 0.85)
    .from(".lp-stats .lp-stat", { y: 22, autoAlpha: 0, stagger: 0.08, clearProps: "all" }, 0.85)
    .from(".lp-hero__visual", { y: 70, autoAlpha: 0, scale: 0.96, duration: 1.3, ease: "power4.out" }, 0.5)
    // The floats keep their CSS bobbing animation (which owns transform),
    // so only fade them in.
    .from(".lp-float", { autoAlpha: 0, stagger: 0.18 }, 1.15);
};

/* "2M+" → counts 0.0M+ … 2M+ the moment the stat scrolls into view. */
const buildCounters = (root) => {
  root.querySelectorAll("[data-count]").forEach((el) => {
    ScrollTrigger.create({
      trigger: el,
      start: "top 94%",
      once: true,
      onEnter: () => {
        const original = el.textContent;
        const m = original.match(/^([^\d]*)([\d][\d,]*(?:\.\d+)?)(.*)$/);
        if (!m) return;
        const target = parseFloat(m[2].replace(/,/g, ""));
        if (!isFinite(target)) return;
        const hasComma = m[2].includes(",");
        const decimals = target < 10 ? 1 : 0;
        const obj = { v: 0 };
        gsap.to(obj, {
          v: target,
          duration: 1.6,
          delay: 0.7,
          ease: "power2.out",
          onUpdate: () => {
            let num = obj.v.toFixed(decimals);
            if (hasComma) num = Number(num).toLocaleString("en-US");
            el.textContent = m[1] + num + m[3];
          },
          // Always land on the exact CMS string, commas and suffix intact.
          onComplete: () => { el.textContent = original; },
        });
      },
    });
  });
};

/* The hero auras drift on their own, sink as you scroll away, and lean
   toward the cursor. (Their CSS keyframes were retired — GSAP owns them.) */
const buildAuras = (root, disposers) => {
  root.querySelectorAll(".lp-aura").forEach((el, i) => {
    gsap.to(el, {
      xPercent: i ? 7 : -6,
      yPercent: i ? -8 : 6,
      scale: 1.12,
      duration: 12 + i * 5,
      yoyo: true,
      repeat: -1,
      ease: "sine.inOut",
    });
  });
  const wrap = root.querySelector(".lp-hero__aura");
  const hero = root.querySelector(".lp-hero");
  if (!wrap || !hero) return;
  gsap.to(wrap, {
    y: 130,
    ease: "none",
    scrollTrigger: { trigger: hero, start: "top top", end: "bottom top", scrub: 0.8 },
  });
  if (finePointer()) {
    const xTo = gsap.quickTo(wrap, "x", { duration: 1.4, ease: "power2" });
    const onMove = (e) => xTo(((e.clientX / window.innerWidth) - 0.5) * 60);
    hero.addEventListener("mousemove", onMove);
    disposers.push(() => hero.removeEventListener("mousemove", onMove));
  }
};

/* Slow Ken Burns drift inside every hero gallery tile — the photographs
   breathe instead of sitting still. */
const buildKenBurns = (root) => {
  root.querySelectorAll(".lp-shot img").forEach((img, i) => {
    gsap.fromTo(img, { scale: 1.03 }, {
      scale: 1.12,
      xPercent: i % 2 ? 1.6 : -1.6,
      duration: 7 + (i % 3) * 2.5,
      yoyo: true,
      repeat: -1,
      ease: "sine.inOut",
      delay: i * 0.5,
    });
  });
};

/* Depth on scroll: the hero panel eases upward as it leaves; the album
   spread glides against its benefit list. */
const buildParallax = (root) => {
  const hero = root.querySelector(".lp-hero");
  if (hero && root.querySelector(".lp-hero__visual")) {
    gsap.to(".lp-hero__visual", {
      y: -54,
      ease: "none",
      scrollTrigger: { trigger: hero, start: "top top", end: "bottom top", scrub: 0.6 },
    });
  }
  const review = root.querySelector(".lp-review");
  const visual = root.querySelector(".lp-review__visual");
  if (review && visual) {
    // Not [data-reveal] — parallax owns its y, so fade in separately.
    gsap.from(visual, {
      autoAlpha: 0,
      duration: 1,
      ease: "power2.out",
      scrollTrigger: { trigger: review, start: "top 82%", once: true },
    });
    gsap.fromTo(visual, { y: 48 }, {
      y: -32,
      ease: "none",
      scrollTrigger: { trigger: review, start: "top bottom", end: "bottom top", scrub: 0.7 },
    });
  }
};

/* Section titles unveil through a clip mask, rising as the mask opens.
   (-8% bottom inset keeps serif descenders unclipped at rest.) */
const buildTitleReveals = (root) => {
  root.querySelectorAll(".lp-shead__title, .lp-cta__title").forEach((t) => {
    gsap.fromTo(t, { clipPath: "inset(0% 0% 100% 0%)", y: 44 }, {
      clipPath: "inset(0% 0% -8% 0%)",
      y: 0,
      duration: 1.15,
      ease: "power4.out",
      clearProps: "clipPath",
      scrollTrigger: { trigger: t, start: "top 88%", once: true },
    });
  });
};

/* Small character moments: feature icons spring in, step numerals hop. */
const buildPops = (root) => {
  root.querySelectorAll(".lp-bento__icon").forEach((icon) => {
    gsap.from(icon, {
      scale: 0,
      rotation: -14,
      duration: 0.7,
      ease: "back.out(1.8)",
      clearProps: "all",
      scrollTrigger: { trigger: icon, start: "top 85%", once: true },
    });
  });
  const nums = root.querySelectorAll(".lp-step__num");
  if (nums.length) {
    gsap.from(nums, {
      yPercent: 60,
      autoAlpha: 0,
      rotation: 4,
      duration: 0.8,
      stagger: 0.12,
      ease: "back.out(1.6)",
      clearProps: "all",
      scrollTrigger: { trigger: ".lp-steps", start: "top 80%", once: true },
    });
  }
};

/* Fast scrolling shears the marquee tracks, which snap back upright. */
const buildVelocitySkew = (root) => {
  const targets = [
    ...root.querySelectorAll(".lp-qrow__track"),
    root.querySelector(".lp-band__track"),
  ].filter(Boolean);
  if (!targets.length) return;
  const setters = targets.map((t) => gsap.quickSetter(t, "skewX", "deg"));
  const proxy = { skew: 0 };
  ScrollTrigger.create({
    onUpdate: (self) => {
      const skew = gsap.utils.clamp(-5, 5, self.getVelocity() / -450);
      if (Math.abs(skew) > Math.abs(proxy.skew)) {
        proxy.skew = skew;
        gsap.to(proxy, {
          skew: 0,
          duration: 0.9,
          ease: "power3",
          overwrite: true,
          onUpdate: () => setters.forEach((s) => s(proxy.skew)),
        });
      }
    },
  });
};

/* Thin gradient hairline at the very top tracking overall scroll. */
const buildProgress = (root) => {
  const bar = root.querySelector(".lp-progress");
  if (!bar) return;
  gsap.set(bar, { scaleX: 0 });
  gsap.to(bar, {
    scaleX: 1,
    ease: "none",
    scrollTrigger: { start: 0, end: "max", scrub: 0.3 },
  });
};

/* Header ducks out of the way scrolling down, returns scrolling up. */
const buildHeaderDuck = (root) => {
  const header = root.querySelector(".lp-header");
  if (!header) return;
  ScrollTrigger.create({
    start: 0,
    end: "max",
    onUpdate: (self) => {
      const hide = self.direction === 1 && self.scroll() > 420;
      header.classList.toggle("lp-header--hidden", hide);
    },
  });
};

/* Capabilities ribbon — infinite loop that surges with scroll velocity. */
const buildBand = (root) => {
  const track = root.querySelector(".lp-band__track");
  if (!track) return;
  const tween = gsap.to(track, { xPercent: -50, duration: 26, ease: "none", repeat: -1 });
  ScrollTrigger.create({
    start: 0,
    end: "max",
    onUpdate: (self) => {
      const boost = Math.min(Math.abs(self.getVelocity()) / 300, 2.5);
      gsap.to(tween, {
        timeScale: 1 + boost,
        duration: 0.25,
        overwrite: true,
        onComplete: () => gsap.to(tween, { timeScale: 1, duration: 1.4 }),
      });
    },
  });
};

/* Testimonials — two counter-flowing marquee rows, pausing under the cursor. */
const buildQuoteRows = (root, disposers) => {
  root.querySelectorAll(".lp-qrow").forEach((row, i) => {
    const track = row.querySelector(".lp-qrow__track");
    if (!track) return;
    const tween = i % 2
      ? gsap.fromTo(track, { xPercent: -50 }, { xPercent: 0, duration: 52, ease: "none", repeat: -1 })
      : gsap.to(track, { xPercent: -50, duration: 52, ease: "none", repeat: -1 });
    const pause = () => gsap.to(tween, { timeScale: 0, duration: 0.5, overwrite: true });
    const play = () => gsap.to(tween, { timeScale: 1, duration: 0.5, overwrite: true });
    row.addEventListener("mouseenter", pause);
    row.addEventListener("mouseleave", play);
    disposers.push(() => {
      row.removeEventListener("mouseenter", pause);
      row.removeEventListener("mouseleave", play);
    });
  });
};

/* The client's red ink draws itself as the album-review section scrolls by. */
const buildInk = (root) => {
  const ink = root.querySelector(".lp-proof__ink");
  if (!ink) return;
  const ellipse = ink.querySelector("ellipse");
  const arrow = ink.querySelector("path");
  const note = root.querySelector(".lp-proof__note");
  if (!ellipse || !arrow) return;
  gsap.set(ellipse, { strokeDashoffset: 252 });
  gsap.set(arrow, { strokeDashoffset: 60 });
  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: ".lp-review__visual",
      start: "top 72%",
      end: "top 24%",
      scrub: 1,
    },
  });
  tl.to(ellipse, { strokeDashoffset: 0, ease: "none", duration: 0.6 }, 0)
    .to(arrow, { strokeDashoffset: 0, ease: "none", duration: 0.25 }, 0.62);
  if (note) tl.from(note, { autoAlpha: 0, y: 12, duration: 0.13 }, 0.87);
};

/* Each step's violet rule sweeps in, one after the other. */
const buildSteps = (root) => {
  const steps = root.querySelectorAll(".lp-step");
  if (!steps.length) return;
  steps.forEach((el, i) => {
    gsap.fromTo(el, { "--step-rule": 0 }, {
      "--step-rule": 1,
      duration: 1,
      ease: "power3.inOut",
      delay: i * 0.14,
      scrollTrigger: { trigger: ".lp-steps", start: "top 82%", once: true },
    });
  });
};

/* Closing CTA panel eases up to full size as it arrives. */
const buildCta = (root) => {
  const cta = root.querySelector(".lp-cta");
  if (!cta) return;
  // Title excluded — it has its own clip-mask unveil in buildTitleReveals.
  gsap.from(".lp-cta__inner > *:not(.lp-cta__title)", {
    y: 34,
    autoAlpha: 0,
    stagger: 0.1,
    duration: 1,
    ease: "power3.out",
    clearProps: "all",
    scrollTrigger: { trigger: cta, start: "top 78%", once: true },
  });
  gsap.fromTo(cta, { scale: 0.955, y: 26 }, {
    scale: 1,
    y: 0,
    ease: "power2.out",
    scrollTrigger: { trigger: cta, start: "top 95%", end: "top 45%", scrub: 0.6 },
  });
  gsap.to(".lp-cta__aura", { scale: 1.18, duration: 9, yoyo: true, repeat: -1, ease: "sine.inOut" });
};

/* Desktop, mouse-only flourishes: hero panel tilt, magnetic CTAs,
   cursor spotlight over the bento grid. */
const buildPointerFx = (root, disposers) => {
  if (!finePointer()) return;

  const hero = root.querySelector(".lp-hero");
  const visual = root.querySelector(".lp-hero__visual");
  if (hero && visual) {
    gsap.set(visual, { transformPerspective: 1100 });
    const rx = gsap.quickTo(visual, "rotationX", { duration: 0.8, ease: "power3" });
    const ry = gsap.quickTo(visual, "rotationY", { duration: 0.8, ease: "power3" });
    const onMove = (e) => {
      const r = hero.getBoundingClientRect();
      rx(-((e.clientY - (r.top + r.height / 2)) / r.height) * 5);
      ry(((e.clientX - (r.left + r.width / 2)) / r.width) * 7);
    };
    const onLeave = () => { rx(0); ry(0); };
    hero.addEventListener("mousemove", onMove);
    hero.addEventListener("mouseleave", onLeave);
    disposers.push(() => {
      hero.removeEventListener("mousemove", onMove);
      hero.removeEventListener("mouseleave", onLeave);
    });
  }

  root.querySelectorAll("[data-magnetic]").forEach((btn) => {
    const xTo = gsap.quickTo(btn, "x", { duration: 0.4, ease: "power3" });
    const yTo = gsap.quickTo(btn, "y", { duration: 0.4, ease: "power3" });
    const onMove = (e) => {
      const r = btn.getBoundingClientRect();
      xTo((e.clientX - (r.left + r.width / 2)) * 0.32);
      yTo((e.clientY - (r.top + r.height / 2)) * 0.5);
    };
    const onLeave = () => gsap.to(btn, { x: 0, y: 0, duration: 0.7, ease: "elastic.out(1, 0.45)" });
    btn.addEventListener("mousemove", onMove);
    btn.addEventListener("mouseleave", onLeave);
    disposers.push(() => {
      btn.removeEventListener("mousemove", onMove);
      btn.removeEventListener("mouseleave", onLeave);
    });
  });

  const bento = root.querySelector(".lp-bento");
  if (bento) {
    const onMove = (e) => {
      const card = e.target.closest(".lp-bento__card");
      if (!card) return;
      const r = card.getBoundingClientRect();
      card.style.setProperty("--mx", `${e.clientX - r.left}px`);
      card.style.setProperty("--my", `${e.clientY - r.top}px`);
    };
    bento.addEventListener("mousemove", onMove);
    disposers.push(() => bento.removeEventListener("mousemove", onMove));
  }
};

/* Public API. Init once on mount; refresh() after async content renders. */
export function initLandingAnimations(root) {
  // Jest/jsdom never advances the ticker, so tweens would strand content
  // hidden — motion is decoration, so skip it under test entirely.
  const isTest = typeof process !== "undefined" && process.env && process.env.NODE_ENV === "test";
  if (!root || typeof window === "undefined" || isTest || prefersReduced()) {
    return { refresh: () => {}, cleanup: () => {} };
  }

  const disposers = [];
  let ctx;
  try {
    ensureRegistered();
    ctx = gsap.context(() => {
      buildIntro(root);
      armReveals(root);
      buildCounters(root);
      buildProgress(root);
      buildHeaderDuck(root);
      buildAuras(root, disposers);
      buildKenBurns(root);
      buildParallax(root);
      buildTitleReveals(root);
      buildPops(root);
      buildVelocitySkew(root);
      buildBand(root);
      buildQuoteRows(root, disposers);
      buildInk(root);
      buildSteps(root);
      buildCta(root);
      buildPointerFx(root, disposers);
    }, root);
  } catch (e) {
    // Motion is decoration — a failure here must never take the page down.
    ctx = null;
  }

  return {
    refresh: () => {
      if (!ctx) return;
      try {
        ctx.add(() => armReveals(root));
        ScrollTrigger.refresh();
      } catch (e) { /* static content still shows */ }
    },
    cleanup: () => {
      disposers.forEach((fn) => { try { fn(); } catch (e) { /* noop */ } });
      try { ctx && ctx.revert(); } catch (e) { /* noop */ }
      // Un-arm reveals so a remount (StrictMode, back-navigation) re-runs them.
      try {
        root.querySelectorAll("[data-lp-armed]").forEach((el) => el.removeAttribute("data-lp-armed"));
      } catch (e) { /* noop */ }
    },
  };
}
